import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../api';

const errorMessage = (err, fallback) => {
  const msg = err?.response?.data?.error;
  return typeof msg === 'string' && msg ? msg : fallback;
};

// The organisation's private indicators of compromise: list state plus the
// add / delete calls. A failed load is reported through loadError so callers
// never show it as an empty list.
export function usePrivateIocs() {
  const [items, setItems] = useState([]);
  const [limit, setLimit] = useState(500);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/api/private-iocs');
      setItems(data.private_iocs || []);
      setLimit(data.limit || 500);
      setLoadError('');
    } catch (err) {
      setLoadError(errorMessage(err, 'Could not load your indicators'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const add = useCallback(async (form) => {
    setSaving(true);
    setError('');
    try {
      const { data } = await apiClient.post('/api/private-iocs', form);
      const created = data?.private_ioc;
      if (created && typeof created === 'object') {
        setItems((cur) => [created, ...cur]);
      } else {
        // Created, but the response did not carry the row: ask the server for the list.
        await reload();
      }
      return true;
    } catch (err) {
      setError(errorMessage(err, 'Could not add the indicator'));
      return false;
    } finally {
      setSaving(false);
    }
  }, [reload]);

  const remove = useCallback(async (item) => {
    setDeleteError('');
    try {
      await apiClient.delete(`/api/private-iocs/${item.id}`);
      setItems((cur) => cur.filter((i) => i.id !== item.id));
      return true;
    } catch (err) {
      setDeleteError(errorMessage(err, 'Could not delete the indicator'));
      return false;
    }
  }, []);

  const clearError = useCallback(() => setError(''), []);
  const clearDeleteError = useCallback(() => setDeleteError(''), []);

  // error is for add failures, deleteError for delete failures; loadError is separate.
  return { items, limit, loading, loadError, error, deleteError, saving, reload, add, remove, clearError, clearDeleteError };
}
